import { useQueryClient } from "@tanstack/react-query";
import { AnimatedModal } from "../../components/AnimatedModal";
import { CloseIcon } from "../../components/CloseIcon";
import { H4 } from "../../components/Heading";
import {
  AddPeopleFromFirmQuery,
  DealRoleType,
  FirmQuery,
  useAddPeopleFromFirmQuery,
  useCreateDealAccountsMutation,
  useFirmQuery,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { useSelector } from "react-redux";
import { authSelectors } from "../../store/auth/selector";
import Loading from "../../components/Loading";
import { useState } from "react";
import { Button } from "../../components/tailwind/Button";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import {
  DealRoleOption,
  DealRolesDropdown,
} from "@/src/components/permissions/DealRolesDropdown";

export function AddPeopleFromFirmModal(props: {
  open: boolean;
  onClose: () => void;
  dealId: string;
}) {
  const client = useGqlClient();
  const addPeopleFromFirm = useAddPeopleFromFirmQuery(client, {
    dealId: props.dealId,
  });

  if (addPeopleFromFirm.error) {
    return null;
  }

  if (addPeopleFromFirm.isPending || !addPeopleFromFirm.data) {
    return null;
  }

  return (
    <AnimatedModal size="lg" open={props.open} onClose={props.onClose}>
      <PermissionCheck input={{ permissionKey: "access:create" }}>
        <div>
          <div className="flex items-center justify-between">
            <H4>
              Add people from your firm to{" "}
              {addPeopleFromFirm.data.deal.company.name}
            </H4>
            <CloseIcon onClose={props.onClose} />
          </div>
          <AddPeopleFromFirmContent
            onClose={props.onClose}
            query={addPeopleFromFirm.data}
            dealId={props.dealId}
          />
        </div>
      </PermissionCheck>
    </AnimatedModal>
  );
}

interface AccountToAdd {
  id: string;
  dealRoleId: string;
}

function AddPeopleFromFirmContent(props: {
  query: AddPeopleFromFirmQuery;
  onClose: () => void;
  dealId: string;
}) {
  const account = useSelector(authSelectors.account);
  const client = useGqlClient();
  const queryClient = useQueryClient();

  const [accountsToAdd, setAccountsToAdd] = useState<AccountToAdd[]>([]);

  const createDealAccounts = useCreateDealAccountsMutation(client);

  const firmQuery = useFirmQuery(client, {
    id: account && account.firm ? account.firm.id : "",
  });

  if (firmQuery.error) {
    return <div>Error</div>;
  }

  if (firmQuery.isPending || !firmQuery.data) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  const availableAccounts = firmQuery.data.firm.accounts.filter((acc) => {
    return !props.query.deal.dealAccounts.some(
      (dealAcc) => dealAcc.account.id === acc.id,
    );
  });

  return (
    <div>
      <div className="mt-3">
        {availableAccounts.length === 0 ? (
          <p className="text-gray-500">No accounts available to add</p>
        ) : null}
        {availableAccounts.map((acc) => {
          return (
            <div key={acc.id} className="py-2">
              <Account
                account={acc}
                dealId={props.dealId}
                accountsToAdd={accountsToAdd}
                setAccountsToAdd={setAccountsToAdd}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-8 flex justify-end">
        <Button
          variant="positive"
          text="Add"
          isDisabled={
            accountsToAdd.length === 0 ||
            accountsToAdd.some((a) => a.dealRoleId === "")
          }
          isLoading={createDealAccounts.isPending}
          loadingText="Adding..."
          onClick={() => {
            createDealAccounts.mutate(
              {
                input: {
                  dealGroupId:
                    props.query.deal.activeDealAccount.dealGroup?.id ?? "",
                  dealId: props.dealId,
                  accounts: accountsToAdd.map((a) => {
                    return {
                      accountId: a.id,
                      dealRoleId: a.dealRoleId,
                      ndaSigned: true,
                    };
                  }),
                },
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: [
                      "DealGroup",
                      {
                        id:
                          props.query.deal.activeDealAccount.dealGroup?.id ??
                          "",
                      },
                    ],
                  });

                  queryClient.invalidateQueries({
                    queryKey: [
                      "Firm",
                      {
                        id: account && account.firm ? account.firm.id : "",
                      },
                    ],
                  });

                  queryClient.invalidateQueries({
                    queryKey: [
                      "AddPeopleFromFirm",
                      {
                        dealId: props.dealId,
                        firmId: account?.firm?.id ?? "",
                      },
                    ],
                  });

                  props.onClose();
                },
              },
            );
          }}
        />
      </div>
    </div>
  );
}

function Account(props: {
  account: FirmQuery["firm"]["accounts"][0];
  dealId: string;
  accountsToAdd: AccountToAdd[];
  setAccountsToAdd: React.Dispatch<React.SetStateAction<AccountToAdd[]>>;
}) {
  const entry = props.accountsToAdd.find((a) => a.id === props.account.id);
  const isChecked = !!entry;
  const [selectedRole, setSelectedRole] = useState<DealRoleOption | null>(null);

  function updateRole(role: DealRoleOption) {
    setSelectedRole(role);
    if (isChecked) {
      props.setAccountsToAdd((prev) =>
        prev.map((a) =>
          a.id === props.account.id ? { ...a, dealRoleId: role.id } : a,
        ),
      );
    }
  }

  return (
    <div>
      <div className="relative flex items-start">
        <div className="flex h-6 items-start">
          <input
            id={`account_to_add_checkbox_${props.account.id}`}
            aria-describedby="comments-description"
            name="account_to_add_checkbox"
            type="checkbox"
            checked={isChecked}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
            onChange={(e) => {
              const checked = e.currentTarget.checked;
              if (checked) {
                props.setAccountsToAdd((prev) => [
                  ...prev,
                  {
                    id: props.account.id,
                    dealRoleId: selectedRole?.id ?? "",
                  },
                ]);
              } else {
                props.setAccountsToAdd((prev) =>
                  prev.filter((a) => a.id !== props.account.id),
                );
              }
            }}
          />
        </div>
        <div className="ml-3 text-sm leading-tight">
          <label
            htmlFor={`account_to_add_checkbox_${props.account.id}`}
            className="font-medium text-gray-900"
          >
            {props.account.name}
          </label>
          <p id="account_to_add_email" className="text-gray-500">
            {props.account.email}
          </p>
        </div>
      </div>
      {isChecked ? (
        <div className="ml-7 mt-3">
          <DealRolesDropdown
            dealId={props.dealId}
            roleType={DealRoleType.Internal}
            value={entry?.dealRoleId || selectedRole?.id}
            onChange={updateRole}
            size="s"
          />
        </div>
      ) : null}
    </div>
  );
}
